import { EntityFieldDef, ValidationResult } from '../models/spreadsheet.model';
import { SPREADSHEET_CONFIG } from '../config/spreadsheet.config';
import { IWorkbookData, IWorksheetData, ICellData, LocaleType } from '@univerjs/core';
import moment from 'moment-timezone';

/**
 * PURE FUNCTION: Parses and converts raw cell input into the expected data type.
 * Specifically maps user-friendly boolean strings (yes/no, 1/0) into actual booleans, 
 * and safely transforms Univer's internal Date Serial Numbers into standard ISO strings.
 * @param {any} value - The raw value entered by the user.
 * @param {string} type - The target field data type.
 * @param {string} [format] - Optional format configuration for formatting the display value.
 * @returns {{ parsedValue: any, isValid: boolean, displayValue?: any }} The converted value, validation flag, and formatted visual string.
 */
export function parseAndConvertCellValue(value: any, type: string, format?: string): { parsedValue: any, isValid: boolean, displayValue?: any } {
  if (value === null || value === undefined || value === '') {
    return { parsedValue: value, isValid: true, displayValue: '' };
  }

  if (type === 'date') {
    let m;
    if (typeof value === 'number') {
      // Excel/Univer stores dates as days since Dec 30, 1899.
      // Convert the serial number to exact milliseconds and map to a UTC moment.
      const msSince1899 = Math.round(value * 86400 * 1000);
      m = moment.utc('1899-12-30T00:00:00.000Z').add(msSince1899, 'ms');
    } else {
      m = moment(value);
    }

    if (m.isValid()) {
      return {
        parsedValue: m.toISOString(),
        isValid: true,
        displayValue: m.format(format || 'lll') // Shown cleanly to user in the spreadsheet cell
      };
    }
    return { parsedValue: value, isValid: false, displayValue: value };
  }

  if (type === 'boolean') {
    if (typeof value === 'boolean') return { parsedValue: value, isValid: true, displayValue: value };
    const str = String(value).trim().toLowerCase();
    if (['1', 'true', 'yes'].includes(str)) return { parsedValue: true, isValid: true, displayValue: true };
    if (['0', 'false', 'no'].includes(str)) return { parsedValue: false, isValid: true, displayValue: false };
    return { parsedValue: value, isValid: false, displayValue: value };
  }

  if (type === 'number') {
    if (typeof value === 'number') return { parsedValue: value, isValid: !isNaN(value), displayValue: value };
    const parsed = Number(value);
    return { parsedValue: isNaN(parsed) ? value : parsed, isValid: !isNaN(parsed), displayValue: isNaN(parsed) ? value : parsed };
  }

  return { parsedValue: String(value), isValid: true, displayValue: String(value) };
}

/**
 * PURE FUNCTION: Parses tabular data from the clipboard (e.g., copied from Excel/Google Sheets).
 * Identifies columns by tabs and rows by newlines.
 * @param {string} text - Raw clipboard text.
 * @returns {string[][]} A 2D array of parsed strings.
 */
export function parseTabularPasteData(text: string): string[][] {
  if (!text) return [];
  return text.split(/\r?\n/).filter(row => row.length > 0).map(row => row.split('\t'));
}

/**
 * PURE FUNCTION: Validates a cell value against its entity field definition rules.
 * @param {any} value - The new value entered by the user.
 * @param {EntityFieldDef} fieldDef - The rules governing this specific column.
 * @returns {ValidationResult} True if valid, or false with a localized error message.
 */
export function validateCellValue(value: any, fieldDef: EntityFieldDef): ValidationResult {
  // 1. Check Required
  if (fieldDef.required && (value === null || value === undefined || value === '')) {
    return { valid: false, errorMessage: SPREADSHEET_CONFIG.texts.requiredField };
  }

  // 2. Skip further validation if empty and not required
  if (value === null || value === undefined || value === '') {
    return { valid: true };
  }

  // 3. Check Data Type
  if (fieldDef.type === 'number' && isNaN(Number(value))) {
    return { valid: false, errorMessage: `${SPREADSHEET_CONFIG.texts.invalidType} Number.` };
  }
  if (fieldDef.type === 'boolean' && typeof value !== 'boolean' && value !== 'true' && value !== 'false') {
    return { valid: false, errorMessage: `${SPREADSHEET_CONFIG.texts.invalidType} Boolean.` };
  }

  // 4. Check Enum
  if (fieldDef.type === 'enum' && fieldDef.enumValues) {
    if (!fieldDef.enumValues.includes(String(value))) {
      return { valid: false, errorMessage: SPREADSHEET_CONFIG.texts.notInEnum };
    }
  }

  return { valid: true };
}

/**
 * PURE FUNCTION: Maps an array of entity data objects into Univer's strict Workbook/CellData format.
 * Converts backend Dates to serial numbers and applies formatting natively.
 * @param {any[]} data - The raw entity data from the backend.
 * @param {EntityFieldDef[]} fields - The column definitions.
 * @returns {IWorkbookData} A complete Univer configuration object ready to be loaded.
 */
export function mapEntityToUniverWorkbook(data: any[], fields: EntityFieldDef[]): IWorkbookData {
  const cellData: { [row: number]: { [col: number]: ICellData } } = {};

  // Row 0: Generate Headers
  cellData[0] = {};
  fields.forEach((field, colIndex) => {
    cellData[0][colIndex] = {
      v: field.title,
      s: { bl: 1, bg: { rgb: '#f3f4f6' } } // Bold and light gray background
    };
  });

  // Row 1 to N: Generate Data
  data.forEach((rowRecord, rowIndex) => {
    const actualRowIndex = rowIndex + 1; // Offset by 1 for the header
    cellData[actualRowIndex] = {};

    fields.forEach((field, colIndex) => {
      let rawValue = rowRecord[field.key];
      let cellType = 1;
      let cellValue: any = '';
      let cellStyle: any = null;

      if (rawValue !== null && rawValue !== undefined) {
        if (field.type === 'date') {
          const m = moment(rawValue);
          if (m.isValid()) {
            // Convert to Excel Serial Number (preserve wall-clock time)
            const wallClockUtc = moment.utc([m.year(), m.month(), m.date(), m.hours(), m.minutes(), m.seconds(), m.milliseconds()]);
            const excelEpochUtc = moment.utc([1899, 11, 30]);

            cellValue = wallClockUtc.diff(excelEpochUtc, 'days', true);
            cellType = 2; // Type 2 = Number

            // Apply native number formatting (fallback to 'mmm d, yyyy h:mm AM/PM' which mimics 'lll')
            cellStyle = { n: { pattern: field.format || 'mmm d, yyyy h:mm AM/PM' } };
          } else {
            cellValue = String(rawValue);
          }
        } else if (typeof rawValue === 'number') {
          cellType = 2;
          cellValue = rawValue;
        } else if (typeof rawValue === 'boolean') {
          cellType = 3;
          cellValue = rawValue ? 1 : 0; // Booleans must be 1 or 0
        } else if (typeof rawValue === 'object') {
          cellValue = JSON.stringify(rawValue);
        } else {
          cellValue = String(rawValue);
        }
      }

      const cellPayload: ICellData = {
        v: cellValue,
        t: cellType
      };

      if (cellStyle) {
        cellPayload.s = cellStyle;
      }

      cellData[actualRowIndex][colIndex] = cellPayload;
    });
  });

  // Map Column Widths
  const columnData: any = {};
  fields.forEach((field, colIndex) => {
    columnData[colIndex] = { hd: 0, w: field.width || 120 };
  });

  return {
    id: SPREADSHEET_CONFIG.univerConfig.id,
    sheetOrder: [SPREADSHEET_CONFIG.univerConfig.sheetId],
    name: SPREADSHEET_CONFIG.univerConfig.name,
    appVersion: '3.0.0',
    locale: LocaleType.EN_US,
    styles: {},
    sheets: {
      [SPREADSHEET_CONFIG.univerConfig.sheetId]: {
        id: SPREADSHEET_CONFIG.univerConfig.sheetId,
        name: SPREADSHEET_CONFIG.univerConfig.sheetName,
        cellData: cellData,
        columnData: columnData,
        rowCount: Math.max(data.length + 10, 100), // Give buffer rows
        columnCount: Math.max(fields.length + 5, 20)
      }
    }
  };
}